import { Event } from './Event';
import { EventListener } from './EventListener';
import { Logger } from '../utils/Logger';

/**
 * Manages event subscriptions and publishing in the engine
 */
export class EventManager {
  private static instance: EventManager | null = null;
  private listeners: Map<string, Set<EventListener>>;
  private eventQueue: Event[];
  private logger: Logger;

  private constructor() {
    this.listeners = new Map();
    this.eventQueue = [];
    this.logger = Logger.getInstance();
  }

  public static getInstance(): EventManager {
    if (!EventManager.instance) {
      EventManager.instance = new EventManager();
    }
    return EventManager.instance;
  }

  /**
   * Subscribe to events of a specific type
   */
  public subscribe(eventType: string, listener: EventListener): void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(listener);
    this.logger.debug(
      `EventManager: Subscribed listener to ${eventType} events`
    );
  }

  /**
   * Unsubscribe from events of a specific type
   */
  public unsubscribe(eventType: string, listener: EventListener): void {
    const eventListeners = this.listeners.get(eventType);
    if (eventListeners) {
      eventListeners.delete(listener);
      this.logger.debug(
        `EventManager: Unsubscribed listener from ${eventType} events`
      );
    }
  }

  /**
   * Publish an event to all subscribed listeners
   */
  public publish(event: Event): void {
    this.eventQueue.push(event);
    this.logger.debug(`EventManager: Published ${event.getType()} event`);
  }

  /**
   * Update the event system, processing all queued events
   */
  public update(): void {
    if (this.eventQueue.length > 0) {
      this.logger.debug(
        `EventManager: Processing ${this.eventQueue.length} queued events`
      );
    }

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()!;
      const eventListeners = this.listeners.get(event.getType());

      this.logger.debug(`EventManager: Processing event: ${event.getType()}`);

      if (eventListeners) {
        this.logger.debug(
          `EventManager: Found ${
            eventListeners.size
          } listeners for ${event.getType()}`
        );
        for (const listener of Array.from(eventListeners)) {
          try {
            listener.onEvent(event);
            this.logger.debug(
              `EventManager: Successfully processed ${event.getType()} event`
            );
          } catch (error) {
            this.logger.error(
              `Error processing ${event.getType()} event: ${error}`
            );
          }
        }
      } else {
        this.logger.warn(
          `EventManager: No listeners found for event type: ${event.getType()}`
        );
      }
    }
  }

  /**
   * Clear all listeners and queued events
   */
  public clear(): void {
    this.listeners.clear();
    this.eventQueue = [];
    this.logger.info('EventManager: Cleared all listeners and events');
  }

  /**
   * Get the number of listeners for a specific event type
   */
  public getListenerCount(eventType: string): number {
    return this.listeners.get(eventType)?.size ?? 0;
  }
}
